import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getMeasurementMatrix } from '../services/BodyMeasurementsService';
import { unitLabel } from '../componenets/measurements/measurementUnits';
import type {
    MeasurementMatrixColumn,
    MeasurementMatrixRow,
} from '../api/generated';
import { parseMediaDate } from './progressMediaDate';

// Calendar day, parsed with no timezone involvement — `new Date("2026-07-15")` parses as UTC
// midnight and the local getters below read it in the viewer's zone, shifting the day west of UTC.
// These dates are baked into an exported PDF, so a silent shift outlives the session.
const formatDate = (dateString: string): string => {
    const date = parseMediaDate(dateString);
    if (!date) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

/**
 * The LOCAL day, for the export filename. `toISOString()` converts to UTC first, so a PDF exported
 * before ~03:00 in Israel was filed under the PREVIOUS day.
 */
export const formatMeasurementDateForFilename = (now: Date = new Date()): string => {
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const exportAllBodyMeasurementsToPdf = async (traineeId: number): Promise<boolean> => {
    try {
        const response = await getMeasurementMatrix(traineeId);
        const matrixData = response.data;

        if (!matrixData || !matrixData.rows || matrixData.rows.length === 0) {
            throw new Error('No measurements data available');
        }

        // Definition-based: hide soft-deleted columns, order by displayOrder.
        const columns: MeasurementMatrixColumn[] = (matrixData.columns || [])
            .filter(col => col.active !== false)
            .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
        const rows: MeasurementMatrixRow[] = matrixData.rows;

        // Load Google Font that supports Hebrew
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;700&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        await document.fonts.ready;
        await new Promise<void>(resolve => setTimeout(resolve, 300));

        // Use landscape for wide tables
        const isWide = columns.length > 6;

        // Build column headers HTML
        const columnHeadersHtml = columns.map(col => {
            const name = col.name;
            const unit = unitLabel(col.unit);
            return `<th style="padding: 8px 6px; text-align: center; border: 1px solid #E2E8F0; font-size: 11px; white-space: nowrap;">
                ${name}<br/><span style="font-size: 9px; font-weight: 400; color: #CBD5E1;">(${unit})</span>
            </th>`;
        }).join('');

        // Build row HTML
        const rowsHtml = rows.map((row, rowIdx) => {
            const bgColor = rowIdx % 2 === 0 ? '#F8FAFC' : '#ffffff';

            const cellsHtml = columns.map(col => {
                const cell = col.key != null ? row.values?.[col.key] : undefined;
                if (!cell) {
                    return `<td style="padding: 6px 4px; text-align: center; border: 1px solid #E2E8F0; font-size: 11px; color: #CBD5E1;">-</td>`;
                }

                let changeHtml = '';
                if (cell.change != null && cell.change !== 0) {
                    const color = cell.change < 0 ? '#14B8A6' : '#EF4444';
                    const sign = cell.change > 0 ? '+' : '';
                    changeHtml = `<br/><span style="font-size: 9px; color: ${color}; font-weight: 500;">${sign}${cell.change.toFixed(1)}</span>`;
                } else if (cell.change === 0) {
                    changeHtml = `<br/><span style="font-size: 9px; color: #94A3B8;">—</span>`;
                }

                return `<td style="padding: 6px 4px; text-align: center; border: 1px solid #E2E8F0; font-size: 11px;">
                    <span style="font-weight: 700; color: #1E293B;">${cell.value?.toFixed(1)}</span>${changeHtml}
                </td>`;
            }).join('');

            return `<tr style="background-color: ${bgColor};">
                <td style="padding: 6px 4px; text-align: center; border: 1px solid #E2E8F0; font-size: 11px; color: #14B8A6; font-weight: 700;">#${row.rowNumber}</td>
                <td style="padding: 6px 4px; text-align: center; border: 1px solid #E2E8F0; font-size: 11px; font-weight: 600; color: #1E293B; white-space: nowrap;">${formatDate(row.measurementDate ?? '')}</td>
                ${cellsHtml}
            </tr>`;
        }).join('');

        // Calculate container width based on columns
        const containerWidth = Math.max(794, 120 + columns.length * 90);

        // Create temporary container
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        container.style.top = '0';
        container.style.width = `${containerWidth}px`;
        container.style.padding = '30px';
        container.style.backgroundColor = '#ffffff';
        container.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
        container.style.direction = 'rtl';

        container.innerHTML = `
            <div style="font-family: 'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; direction: rtl;">
                <!-- Header -->
                <div style="background-color: #14B8A6; color: white; padding: 20px; margin: -30px -30px 20px -30px;">
                    <h1 style="margin: 0 0 5px 0; font-size: 22px; text-align: right;">היסטוריית מדידות גוף</h1>
                    <p style="margin: 3px 0; font-size: 11px; text-align: right;">תאריך: ${new Date().toLocaleDateString('he-IL')}</p>
                </div>

                <!-- Table -->
                <table style="width: 100%; border-collapse: collapse;">
                    <thead>
                        <tr style="background-color: #475569; color: white;">
                            <th style="padding: 8px 6px; text-align: center; border: 1px solid #E2E8F0; font-size: 11px; white-space: nowrap;">#</th>
                            <th style="padding: 8px 6px; text-align: center; border: 1px solid #E2E8F0; font-size: 11px; white-space: nowrap;">תאריך מדידה</th>
                            ${columnHeadersHtml}
                        </tr>
                    </thead>
                    <tbody>
                        ${rowsHtml}
                    </tbody>
                </table>

                <!-- Footer -->
                <div style="margin-top: 15px; display: flex; justify-content: space-between; align-items: center;">
                    <p style="margin: 0; font-size: 10px; color: #94A3B8;">נוצר על ידי Progress System</p>
                    <p style="margin: 0; font-size: 11px; color: #64748B; font-weight: 600;">סה״כ ${rows.length} מדידות</p>
                </div>
            </div>
        `;

        document.body.appendChild(container);

        await new Promise<void>(resolve => setTimeout(resolve, 100));

        // Convert to canvas
        const canvas = await html2canvas(container, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
        });

        document.body.removeChild(container);

        // Determine PDF orientation based on rendered content aspect ratio
        const imgAspect = canvas.width / canvas.height;
        const orientation: 'landscape' | 'portrait' = isWide || imgAspect > 1.2 ? 'landscape' : 'portrait';

        const pdf = new jsPDF({
            orientation,
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = orientation === 'landscape' ? 297 : 210;
        const pageHeight = orientation === 'landscape' ? 210 : 297;

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        // If content fits on one page
        if (imgHeight <= pageHeight) {
            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        } else {
            // Multi-page: slice the canvas into page-height chunks
            const sourcePageHeight = (canvas.width * pageHeight) / pageWidth;
            let remainingHeight = canvas.height;
            let sourceY = 0;
            let isFirst = true;

            while (remainingHeight > 0) {
                if (!isFirst) {
                    pdf.addPage();
                }
                isFirst = false;

                const sliceHeight = Math.min(sourcePageHeight, remainingHeight);
                const sliceCanvas = document.createElement('canvas');
                sliceCanvas.width = canvas.width;
                sliceCanvas.height = sliceHeight;
                const ctx = sliceCanvas.getContext('2d');
                ctx?.drawImage(canvas, 0, sourceY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

                const sliceData = sliceCanvas.toDataURL('image/png');
                const sliceImgHeight = (sliceHeight * imgWidth) / canvas.width;
                pdf.addImage(sliceData, 'PNG', 0, 0, imgWidth, sliceImgHeight);

                sourceY += sliceHeight;
                remainingHeight -= sliceHeight;
            }
        }

        const filename = `body-measurements-${formatMeasurementDateForFilename()}.pdf`;
        pdf.save(filename);

        return true;
    } catch (error) {
        console.error('Error creating PDF:', error);
        throw error;
    }
};
